import { useState } from 'react';

import { useRouter } from 'next/router';

import * as Sentry from '@sentry/browser';
import { useCookies } from 'react-cookie';
import { useRecoilState, useResetRecoilState } from 'recoil';

import { adminLoginUserDataAtom } from '@recoil_iqfu/atoms/auth/adminLoginUserData';
import { CompanySerivces, companyLoginUserDataAtom } from '@recoil_iqfu/atoms/auth/companyLoginUserData';
import {
  FinancialAgencyServices,
  finanialAgencyLoginUserDataAtom,
} from '@recoil_iqfu/atoms/auth/financialAgencyLoginUserData';

import { UserType } from '@constants_common';

const backendBasePath = process.env.NEXT_PUBLIC_DEV_BACKEND_URL;

//cookieの期限 デフォルト180日にセット
//ログイン時にtokenのレスポンスの期限セット
let cookieExpires = 60 * 60 * 24 * 180;

export const useProvideAuth = () => {
  const [cookies, setCookie, removeCookie] = useCookies([
    'expiry',
    'access-token',
    'client',
    'uid',
    'user-type',
    'login_type',
  ]);
  const router = useRouter();

  const [tokenValidity, setTokenValidity] = useState<'unvalidated' | 'invalid' | 'valid' | 'unvalidated'>(
    'unvalidated',
  );

  const [adminLoginUserData, setAdminLoginUserData] = useRecoilState(adminLoginUserDataAtom);

  const [companyLoginUserData, setCompanyLoginUserData] = useRecoilState(companyLoginUserDataAtom);

  const [financialAgencyLoginUserData, setFinancialAgencyLoginUserData] = useRecoilState(
    finanialAgencyLoginUserDataAtom,
  );

  const resetCompanyLoginData = useResetRecoilState(companyLoginUserDataAtom);
  const resetAdminLoginData = useResetRecoilState(adminLoginUserDataAtom);
  const resetFinancialAgencyLoginData = useResetRecoilState(finanialAgencyLoginUserDataAtom);

  const resetLoginData = () => {
    resetCompanyLoginData();
    resetAdminLoginData();
    resetFinancialAgencyLoginData();
  };

  const storeAuthCookies = (response: Response, userType: UserType) => {
    cookieExpires = Number(response.headers.get('expiry'));
    setCookie('expiry', response.headers.get('expiry'), { maxAge: cookieExpires, path: '/' });
    setCookie('access-token', response.headers.get('access-token'), { maxAge: cookieExpires, path: '/' });
    setCookie('client', response.headers.get('client'), { maxAge: cookieExpires, path: '/' });
    setCookie('uid', response.headers.get('uid'), { maxAge: cookieExpires, path: '/' });
    setCookie('user-type', userType, { maxAge: cookieExpires, path: '/' });
  };

  const loginRequest = async (formData: FormData, userType: UserType): Promise<any> => {
    const url = `${backendBasePath}/${userType}/sign_in`;

    let status;
    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      body: formData,
    })
      .then((response) => {
        if (response.status === 200) {
          status = 'success';
          storeAuthCookies(response, userType);
          return response.json();
        } else if (response.status === 202) {
          // 202 means the admin login waits for OTP verification; no auth headers are returned.
          status = 'otp_required';
          return response.json();
        } else {
          status = 'error';
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('Error:', error);
      });

    return { status, result };
  };

  const dashboardLogin = async (formData: FormData, userType: UserType): Promise<any> => {
    return new Promise(async (resolve) => {
      removeCookie('expiry', { path: '/' });
      removeCookie('access-token', { path: '/' });
      removeCookie('client', { path: '/' });
      removeCookie('user-type', { path: '/' });
      removeCookie('uid', { path: '/' });
      removeCookie('login_type', { path: '/' });
      setTokenValidity('unvalidated');
      resetLoginData();

      const { status, result } = await loginRequest(formData, userType);

      if (!status) {
        router.push(`/service-unavailable`);
      } else if (status === 'otp_required') {
        resolve({
          success: false,
          otpRequired: true,
          challenge: {
            challengeToken: result.challenge_token,
            expiresAt: result.expires_at,
            emailHint: result.email_hint,
          },
        });
      } else if (status !== 'success') {
        resolve({ success: false, errorCode: result?.error_code });
      } else {
        if (userType === 'admin') {
          setAdminLoginUserData(result.data);
        }
        if (userType === 'company') {
          setCompanyLoginUserData(result.data);
        }
        if (userType === 'financial_agency') {
          setFinancialAgencyLoginUserData(result.data);
        }

        setTokenValidity('valid');

        resolve({ data: result.data, success: true });
      }
    });
  };

  const verifyAdminOtp = async (challengeToken: string, otp: string): Promise<any> => {
    const url = `${backendBasePath}/admin/sign_in/otp/verify`;

    let status: number | undefined;
    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ challenge_token: challengeToken, otp }),
    })
      .then((response) => {
        status = response.status;
        if (response.status === 200) {
          storeAuthCookies(response, 'admin');
        }
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('Error:', error);
      });

    if (!status) {
      router.push(`/service-unavailable`);
      return;
    }

    if (status === 200) {
      setAdminLoginUserData(result.data);
      setTokenValidity('valid');
      return { success: true, data: result.data };
    }

    return {
      success: false,
      status,
      errorCode: result?.error_code,
    };
  };

  const isAuthenticated = async (params: { userType: UserType; pagePath: string }): Promise<boolean> => {
    return new Promise(async (resolve) => {
      let hasValidToken = false;
      if (tokenValidity === 'unvalidated' || tokenValidity === 'valid') {
        hasValidToken = await validateToken({ userType: params.userType, pagePath: params.pagePath });
      } else {
        hasValidToken = false;
      }
      return resolve(hasValidToken);
    });
  };

  const validateToken = async (params: { userType: UserType; pagePath: string }): Promise<boolean> => {
    return new Promise(async (resolve) => {
      const validateTokenRequest = async (): Promise<any> => {
        const url = `${backendBasePath}/${params.userType}/validate_token?page_path=${params.pagePath}`;

        const result = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          headers: {
            uid: cookies['uid'],
            client: cookies['client'],
            'access-token': cookies['access-token'],
          },
        })
          .then((response) => {
            return response.json();
          })
          .catch((error) => {
            Sentry.captureException(error);
            console.error('Error:', error);
          });
        return result;
      };
      const result = await validateTokenRequest();

      //serviceの更新処理
      //serviceの更新があった場合、ログアウトなしで更新する
      if (result?.data) {
        if (userType === 'company') {
          companyLoginUserData &&
            setCompanyLoginUserData({
              ...companyLoginUserData,
              services: result.data.services as CompanySerivces[],
              company_type: result.data.company_type,
              commission_rate_type_key: result.data.commission_rate_type_key,
            });
        }
        if (userType === 'financial_agency') {
          financialAgencyLoginUserData &&
            setFinancialAgencyLoginUserData({
              ...financialAgencyLoginUserData,
              services: result.data.services as FinancialAgencyServices[],
            });
        }
      }

      result && result.success ? setTokenValidity('valid') : setTokenValidity('invalid');

      return resolve(result?.success);
    });
  };

  const logout = (userType: string) => {
    const url = `${backendBasePath}/${userType}/sign_out`;
    const logoutRequest = async (): Promise<any> => {
      const result = await fetch(url, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          uid: cookies['uid'],
          client: cookies['client'],
          'access-token': cookies['access-token'],
        },
      })
        .then((response) => {
          return response.json();
        })
        .catch((error) => {
          Sentry.captureException(error);
          console.error('Error:', error);
        });
      return result;
    };
    (async () => {
      const result = await logoutRequest();
      if (!result.success) {
        console.error('logout request failed');
      }
      router.push(`/console/${userType}/login`);

      removeCookie('expiry', { path: '/' });
      removeCookie('access-token', { path: '/' });
      removeCookie('client', { path: '/' });
      removeCookie('user-type', { path: '/' });
      removeCookie('uid', { path: '/' });
      setTokenValidity('unvalidated');

      resetLoginData();
    })();
  };

  const userType = cookies['user-type'];

  return {
    userType,
    dashboardLogin,
    verifyAdminOtp,
    isAuthenticated,
    logout,
  };
};
